import React, { useEffect, useState } from 'react';
import {
  Grid,
  Paper,
  Typography,
  Card,
  CardContent,
  Box,
  withStyles,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
} from '@material-ui/core';
import Skeleton from '@material-ui/lab/Skeleton';
import { connect } from 'react-redux';
import { appService } from '../../App/app.service';
import ForecastTrendChart from './ForecastTrendChart';
import { CumulativeClosedWonChart, PipelineByStageChart, ForecastAccuracyChart } from './DealProgressionCharts';
import { PipelineAgingChart, ConversionCycletimeChart, QualificationConversionChart, POCConversionChart } from './DealConversionCharts';
import UpsellCrossSellChart from './UpsellCrossSellChart';
import SalesForecastCurrentQuarterTable from './SalesForecastCurrentQuarterTable';
import { countOrACVFormatterMillionFixedOne } from '../../util/customFunctions';
import * as d3 from 'd3';
import { updatedScheme10 } from '../../util/chartColorScheme';
import GlobalDownloadButton from '../../components/GlobalDownloadButton';

const styles = (theme) => ({
  root: {
    padding: theme.spacing(3),
    backgroundColor: '#fafafa',
    minHeight: '100vh',
  },
  header: {
    marginBottom: theme.spacing(3),
  },
  title: {
    fontWeight: 600,
    color: '#333',
  },
  subtitle: {
    color: '#666',
    fontSize: '0.875rem',
  },
  paper: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  chartCard: {
    height: '100%',
    position: 'relative',
  },
  chartContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: 250,
  },
  legend: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: theme.spacing(3),
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  legendItem: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  legendBox: {
    width: 16,
    height: 16,
    borderRadius: 2,
  },
  statusBadge: {
    position: 'absolute',
    top: 8,
    right: 8,
    padding: '2px 8px',
    borderRadius: 4,
    fontSize: '0.75rem',
    fontWeight: 500,
  },
  elapsedBadge: {
    backgroundColor: '#9e9e9e',
    color: '#fff',
  },
  currentBadge: {
    backgroundColor: '#4caf50',
    color: '#fff',
  },
  futureBadge: {
    backgroundColor: '#2196f3',
    color: '#fff',
  },
  summaryCard: {
    backgroundColor: '#fff',
    marginTop: theme.spacing(2),
  },
  summaryItem: {
    textAlign: 'center',
    padding: theme.spacing(2),
  },
  summaryValue: {
    fontSize: '1.5rem',
    fontWeight: 600,
    color: '#1976d2',
  },
  summaryLabel: {
    fontSize: '0.875rem',
    color: '#666',
  },
  updateFrequency: {
    backgroundColor: '#00bcd4',
    color: '#fff',
    padding: '4px 12px',
    borderRadius: 4,
    fontSize: '0.75rem',
    fontWeight: 500,
    marginLeft: theme.spacing(2),
  },
  currentQuarterInfo: {
    marginTop: theme.spacing(1),
  },
  // Paper 2 Table Styles
  tableContainer: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  tableHeader: {
    backgroundColor: '#1976d2',
    '& th': {
      color: '#fff',
      fontWeight: 600,
      fontSize: '0.875rem',
      padding: '12px 8px',
      borderRight: '1px solid rgba(255,255,255,0.2)',
      '&:last-child': {
        borderRight: 'none',
      },
    },
  },
  tableRow: {
    '&:nth-of-type(odd)': {
      backgroundColor: '#fafafa',
    },
    '&:hover': {
      backgroundColor: '#f5f5f5',
    },
  },
  newOpportunityRow: {
    backgroundColor: '#c8e6c9 !important',
    '&:hover': {
      backgroundColor: '#a5d6a7 !important',
    },
  },
  tableCell: {
    fontSize: '0.813rem',
    padding: '8px',
    borderRight: '1px solid #e0e0e0',
    '&:last-child': {
      borderRight: 'none',
    },
  },
  summaryRow: {
    backgroundColor: '#f5f5f5',
    '& td': {
      fontWeight: 600,
      fontSize: '0.875rem',
    },
  },
  targetRow: {
    backgroundColor: '#fff3cd',
    '& td': {
      fontWeight: 600,
      fontSize: '0.875rem',
    },
  },
  notesBox: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(2),
    backgroundColor: '#fffbea',
    borderRadius: 4,
    border: '1px solid #ffd54f',
  },
  noteItem: {
    fontSize: '0.75rem',
    marginBottom: theme.spacing(0.5),
  },
  leftNote: {
    backgroundColor: '#fff9e6',
    padding: theme.spacing(2),
    borderRadius: 4,
    fontSize: '0.75rem',
    lineHeight: 1.6,
  },
  filterContainer: {
    marginBottom: theme.spacing(2),
    padding: theme.spacing(1.5),
  },
  teamFilterFormControl: {
    minWidth: 200,
  },
  filterLabel: {
    color: '#000',
  },
  filterLabelActive: {
    color: '#4472c4',
  },
});

const stickyHeaderStyle = {
  backgroundColor: '#1976d2',
  color: '#fff',
  fontWeight: 600,
  fontSize: '0.875rem',
  padding: '12px 8px',
  borderRight: '1px solid rgba(255,255,255,0.2)',
  textAlign: 'center',
  position: 'sticky',
  top: 0,
  zIndex: 2,
};

const RegionalSalesReviewPackage = (props) => {
  const { classes, forecastTrendData, salesForecastCurrentQuarterData, forecastMovedOutData, salesForecastNextQuarterData, salesForecastNextQuarterPlus1Data, dealProgressionData, dealConversionData, upsellCrossSellData, rsrFirstLevelTeams, rsrSelectedTeam } = props;

  // Local state for pending team selection (before clicking Go)
  const [pendingTeam, setPendingTeam] = useState('All');
  // Applied team filter (after clicking Go)
  const [appliedTeam, setAppliedTeam] = useState('All');
  // Track if initial data has been loaded
  const [initialLoadDone, setInitialLoadDone] = useState(false);

  // Fetch first-level teams on mount
  useEffect(() => {
    props.getFirstLevelTeams();
  }, []);

  // Set default team when teams are loaded (first team if available, else 'All')
  useEffect(() => {
    if (rsrFirstLevelTeams && rsrFirstLevelTeams.length > 0 && !initialLoadDone) {
      const defaultTeam = rsrFirstLevelTeams[0] || '';
      setPendingTeam(defaultTeam);
      setAppliedTeam(defaultTeam);
      // Fetch data with default team
      const filters = defaultTeam ? [{ name: 'team', value: defaultTeam }] : [];
      props.getForecastTrendData(filters);
      props.getSalesForecastCurrentQuarterData(filters);
      props.getForecastMovedOutData(filters);
      props.getSalesForecastNextQuarterData(filters);
      props.getSalesForecastNextQuarterPlus1Data(filters);
      props.getDealProgressionData(filters);
      props.getDealConversionData(filters);
      props.getUpsellCrossSellData(filters);
      setInitialLoadDone(true);
    } else if (rsrFirstLevelTeams && rsrFirstLevelTeams.length === 0 && !initialLoadDone) {
      // No teams available, load with 'All'
      setPendingTeam('All');
      setAppliedTeam('All');
      props.getForecastTrendData([]);
      props.getSalesForecastCurrentQuarterData([]);
      props.getForecastMovedOutData([]);
      props.getSalesForecastNextQuarterData([]);
      props.getSalesForecastNextQuarterPlus1Data([]);
      props.getDealProgressionData([]);
      props.getDealConversionData([]);
      props.getUpsellCrossSellData([]);
      setInitialLoadDone(true);
    }
  }, [rsrFirstLevelTeams, initialLoadDone]);

  // Handle team filter change (just updates pending, doesn't fetch)
  const handleTeamChange = (event) => {
    setPendingTeam(event.target.value);
  };

  // Handle Go button click - apply the filter and fetch data
  const handleGo = () => {
    setAppliedTeam(pendingTeam);
    const filters = pendingTeam !== 'All' ? [{ name: 'team', value: pendingTeam }] : [];
    props.getForecastTrendData(filters);
    props.getSalesForecastCurrentQuarterData(filters);
    props.getForecastMovedOutData(filters);
    props.getSalesForecastNextQuarterData(filters);
    props.getSalesForecastNextQuarterPlus1Data(filters);
    props.getDealProgressionData(filters);
    props.getDealConversionData(filters);
    props.getUpsellCrossSellData(filters);
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'elapsed':
        return classes.elapsedBadge;
      case 'current':
        return classes.currentBadge;
      default:
        return classes.futureBadge;
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'elapsed':
        return 'Completed';
      case 'current':
        return 'Current';
      default:
        return 'Future';
    }
  };

  const isForecastTrendLoading = forecastTrendData === "";
  const isSalesForecastCurrentQuarterLoading = salesForecastCurrentQuarterData === "";
  const isForecastMovedOutLoading = forecastMovedOutData === "";
  const isSalesForecastNextQuarterLoading = salesForecastNextQuarterData === "";
  const isSalesForecastNextQuarterPlus1Loading = salesForecastNextQuarterPlus1Data === "";
  const isDealProgressionLoading = dealProgressionData === "";
  const isUpsellCrossSellLoading = upsellCrossSellData === "";

  return (
    <div className={classes.root}>
      {/* Team Filter */}
      <Grid container component={Paper} className={classes.filterContainer} alignItems="center" spacing={2}>
        <Grid item xs={12} sm={6} md={3} lg={2}>
          <FormControl fullWidth className={classes.teamFilterFormControl}>
            <InputLabel
              id="team-filter-label"
              style={{
                color: pendingTeam !== '' ? '#4472c4' : '#000',
              }}
            >
              Team:
            </InputLabel>
            <Select
              labelId="team-filter-label"
              id="team-filter"
              value={pendingTeam}
              onChange={handleTeamChange}
              label="Team:"
            >
              <MenuItem value="All">
                All
              </MenuItem>
              {rsrFirstLevelTeams && rsrFirstLevelTeams.map((team) => (
                <MenuItem key={team} value={team}>
                  {team}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item>
          <Button
            color="primary"
            variant="contained"
            onClick={handleGo}
          >
            GO
          </Button>
        </Grid>
      </Grid>

      {/* Paper 1: Forecast Trend Charts */}
      <Paper className={classes.paper}>
        <div className={classes.header}>
          <GlobalDownloadButton
            dashboardTitle={`Regional Sales Review Package`}
            fileName={`Regional_Sales_Review_Package`}
          />
          <Box display="flex" flexDirection='column' alignItems="center">
            <Typography variant="h5" className={classes.title}>
              {forecastTrendData?.title || 'Forecast Trend'} ($M)
            </Typography>
            <span className={classes.updateFrequency}>
              Weekly update - Every Friday PM
            </span>
          </Box>
        </div>

        {/* Quarterly Charts - 2x2 Grid with Loading */}
        {isForecastTrendLoading ? (
          <Grid container spacing={3}>
            {[1, 2, 3, 4].map((index) => (
              <Grid item xs={12} sm={6} md={6} lg={6} key={index}>
                <Card className={classes.chartCard}>
                  <CardContent>
                    <Skeleton variant="rect" animation="wave" height={300} width="100%" />
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        ) : (
          <Grid container spacing={3}>
            {forecastTrendData?.quarters?.map((quarter) => (
              <Grid item xs={12} sm={6} md={6} lg={6} key={quarter.quarter}>
                <Card className={classes.chartCard}>
                  <CardContent>
                    <div className={classes.chartContainer}>
                      <ForecastTrendChart
                        data={quarter.weeklyData}
                        quarterLabel={quarter.quarter}
                        status={quarter.status}
                        target={quarter.target}
                      />
                    </div>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}

        {/* Legend */}
        <Box mt={3}>
          <div className={classes.legend}>
            <div className={classes.legendItem}>
              <div className={classes.legendBox} style={{ backgroundColor: '#70ad47' }} />
              <Typography variant="body2">Won</Typography>
            </div>
            <div className={classes.legendItem}>
              <div className={classes.legendBox} style={{ backgroundColor: '#c5e0b4' }} />
              <Typography variant="body2">Most Likely</Typography>
            </div>
            <div className={classes.legendItem}>
              <div
                className={classes.legendBox}
                style={{ backgroundColor: '#bf9000', height: 3, width: 24 }}
              />
              <Typography variant="body2">Target</Typography>
            </div>
          </div>
        </Box>
      </Paper>

      {/* Paper 2: Sales Forecast Current Quarter */}
      <SalesForecastCurrentQuarterTable
        classes={classes}
        data={salesForecastCurrentQuarterData}
        isLoading={isSalesForecastCurrentQuarterLoading}
      />

      {/* Paper 4: Forecast Moved Out Opps */}
      <Box mt={4}>
        <Paper className={classes.paper}>
          <Box display="flex" flexDirection="column" alignItems="center" mb={2}>
            <Typography variant="h6" style={{ fontWeight: 600 }}>
              Sales Forecast – Current Quarter – Forecast – Most Likely + Commit Opps moved out in the last week
            </Typography>
            <span className={classes.updateFrequency}>
              Weekly update - Every Friday PM
            </span>
          </Box>

          {isForecastMovedOutLoading ? (
            <Skeleton variant="rect" animation="wave" height={200} width="100%" />
          ) : (
            <>
              <TableContainer className={classes.tableContainer} style={{ maxHeight: '80vh', overflow: 'auto' }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell style={stickyHeaderStyle}>#</TableCell>
                      <TableCell style={stickyHeaderStyle}>Opportunity</TableCell>
                      <TableCell style={stickyHeaderStyle}>Owner</TableCell>
                      <TableCell style={stickyHeaderStyle}>ACV ($K)</TableCell>
                      <TableCell style={stickyHeaderStyle}>Disc. %</TableCell>
                      <TableCell style={stickyHeaderStyle}>Projected<br/>Close Date</TableCell>
                      <TableCell style={stickyHeaderStyle}>Age since<br/>Qual. (Days)</TableCell>
                      <TableCell style={stickyHeaderStyle}>Stage</TableCell>
                      <TableCell style={stickyHeaderStyle}>Days in<br/>Stage</TableCell>
                      <TableCell style={stickyHeaderStyle}>Partner</TableCell>
                      <TableCell style={stickyHeaderStyle}>Forecast<br/>Category</TableCell>
                      <TableCell style={{ ...stickyHeaderStyle, borderRight: 'none' }}>Next Steps</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {forecastMovedOutData?.movedOutOpps?.map((opp, index) => (
                      <TableRow
                        key={opp.oppId || index}
                        className={classes.tableRow}
                      >
                        <TableCell className={classes.tableCell} align="center">{opp.rowNum}</TableCell>
                        <TableCell className={classes.tableCell} align="left">{opp.opportunity}</TableCell>
                        <TableCell className={classes.tableCell} align="left">{opp.owner}</TableCell>
                        <TableCell className={classes.tableCell} align="right">{opp.acvInK}</TableCell>
                        <TableCell className={classes.tableCell} align="right">{/* Disc. % */}</TableCell>
                        <TableCell className={classes.tableCell} align="right">{opp.projectedCloseDate}</TableCell>
                        <TableCell className={classes.tableCell} align="right">{opp.ageSinceQualDays}</TableCell>
                        <TableCell className={classes.tableCell} align="left">{opp.stageChar}</TableCell>
                        <TableCell className={classes.tableCell} align="right">{/* Days in Stage */}</TableCell>
                        <TableCell className={classes.tableCell} align="left">{opp.partner}</TableCell>
                        <TableCell className={classes.tableCell} align="left">{opp.forecastCategory}</TableCell>
                        <TableCell className={classes.tableCell} align="left" style={{ maxWidth: 200, fontSize: '0.75rem' }}>{/* Next Steps */}</TableCell>
                      </TableRow>
                    ))}
                    {(!forecastMovedOutData?.movedOutOpps || forecastMovedOutData.movedOutOpps.length === 0) && (
                      <TableRow>
                        <TableCell colSpan={12} align="center" style={{ fontStyle: 'italic', color: '#666' }}>
                          No forecast opps moved out this week
                        </TableCell>
                      </TableRow>
                    )}
                    {forecastMovedOutData?.movedOutOpps?.length > 0 && (
                      <TableRow className={classes.summaryRow}>
                        <TableCell colSpan={3} className={classes.tableCell}>
                          <strong>Total ({forecastMovedOutData.totalCount} opps)</strong>
                        </TableCell>
                        <TableCell className={classes.tableCell} align="right">
                          <strong>{forecastMovedOutData.totalACVInK}</strong>
                        </TableCell>
                        <TableCell colSpan={8} className={classes.tableCell}></TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </Paper>
      </Box>

      {/* Paper 5: Sales Forecast Next Quarter */}
      <Box mt={4}>
        <Paper className={classes.paper}>
          <Box display="flex" flexDirection="column" alignItems="center" mb={2}>
            <Typography variant="h6" style={{ fontWeight: 600 }}>
              Sales Forecast – Next Quarter{salesForecastNextQuarterData?.targetQuarter ? ` (${salesForecastNextQuarterData.targetQuarter})` : ''} – Opps in Forecast – Most Likely + Commit
            </Typography>
            <span className={classes.updateFrequency}>
              Weekly update - Every Friday PM
            </span>
          </Box>

          {isSalesForecastNextQuarterLoading ? (
            <Skeleton variant="rect" animation="wave" height={200} width="100%" />
          ) : (
            <>
              {/* Summary */}
              <Box mb={2}>
                <TableContainer>
                  <Table size="small" style={{ maxWidth: 500 }}>
                    <TableHead>
                      <TableRow className={classes.tableHeader}>
                        <TableCell style={{ textAlign: 'center' }}></TableCell>
                        <TableCell style={{ textAlign: 'center' }}># of Opps</TableCell>
                        <TableCell style={{ textAlign: 'center' }}>ACV ($M)</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <TableRow>
                        <TableCell align="center"><strong>Forecast</strong></TableCell>
                        <TableCell align="center">{salesForecastNextQuarterData?.totalCount || 0}</TableCell>
                        <TableCell align="center">${salesForecastNextQuarterData?.totalACVInMillions || '0.00'}</TableCell>
                      </TableRow>
                      <TableRow style={{ backgroundColor: '#fff9c4' }}>
                        <TableCell align="center"><strong>Quota</strong></TableCell>
                        <TableCell align="center">-</TableCell>
                        <TableCell align="center"><strong>${salesForecastNextQuarterData?.quotaInMillions || '0.00'}</strong></TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>

              {/* Forecast Opps Table */}
              <TableContainer className={classes.tableContainer} style={{ maxHeight: '80vh', overflow: 'auto' }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell style={stickyHeaderStyle}>#</TableCell>
                      <TableCell style={stickyHeaderStyle}>Opportunity</TableCell>
                      <TableCell style={stickyHeaderStyle}>Owner</TableCell>
                      <TableCell style={stickyHeaderStyle}>ACV ($K)</TableCell>
                      <TableCell style={stickyHeaderStyle}>Disc. %</TableCell>
                      <TableCell style={stickyHeaderStyle}>Projected<br/>Close Date</TableCell>
                      <TableCell style={stickyHeaderStyle}>Age since<br/>Qual. (Days)</TableCell>
                      <TableCell style={stickyHeaderStyle}>Stage</TableCell>
                      <TableCell style={stickyHeaderStyle}>Days in<br/>Stage</TableCell>
                      <TableCell style={stickyHeaderStyle}>Partner</TableCell>
                      <TableCell style={stickyHeaderStyle}>Forecast<br/>Category</TableCell>
                      <TableCell style={{ ...stickyHeaderStyle, borderRight: 'none' }}>Next Steps</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {salesForecastNextQuarterData?.forecastOpps?.slice(0, 15).map((opp, index) => (
                      <TableRow
                        key={opp.oppId || index}
                        className={opp.isNewThisWeek ? classes.newOpportunityRow : classes.tableRow}
                      >
                        <TableCell className={classes.tableCell} align="center">{opp.rowNum}</TableCell>
                        <TableCell className={classes.tableCell} align="left">{opp.opportunity}</TableCell>
                        <TableCell className={classes.tableCell} align="left">{opp.owner}</TableCell>
                        <TableCell className={classes.tableCell} align="right">{opp.acvInK}</TableCell>
                        <TableCell className={classes.tableCell} align="right">{/* Disc. % */}</TableCell>
                        <TableCell className={classes.tableCell} align="right">{opp.projectedCloseDate}</TableCell>
                        <TableCell className={classes.tableCell} align="right">{opp.ageSinceQualDays}</TableCell>
                        <TableCell className={classes.tableCell} align="left">{opp.stageChar}</TableCell>
                        <TableCell className={classes.tableCell} align="right">{/* Days in Stage */}</TableCell>
                        <TableCell className={classes.tableCell} align="left">{opp.partner}</TableCell>
                        <TableCell className={classes.tableCell} align="left">{opp.forecastCategory}</TableCell>
                        <TableCell className={classes.tableCell} align="left" style={{ maxWidth: 200, fontSize: '0.75rem' }}>{/* Next Steps */}</TableCell>
                      </TableRow>
                    ))}
                    {salesForecastNextQuarterData?.forecastOpps?.length > 15 && (
                      <TableRow className={classes.tableRow}>
                        <TableCell className={classes.tableCell} align="center"></TableCell>
                        <TableCell className={classes.tableCell} align="left" colSpan={2}>
                          <strong>Other Opps ({salesForecastNextQuarterData.forecastOpps.length - 15})</strong>
                        </TableCell>
                        <TableCell className={classes.tableCell} align="right">
                          <strong>
                            {salesForecastNextQuarterData.forecastOpps.slice(15).reduce((sum, opp) => sum + parseFloat(opp.acvInK || 0), 0).toFixed(1)}
                          </strong>
                        </TableCell>
                        <TableCell colSpan={8} className={classes.tableCell}></TableCell>
                      </TableRow>
                    )}
                    {(!salesForecastNextQuarterData?.forecastOpps || salesForecastNextQuarterData.forecastOpps.length === 0) && (
                      <TableRow>
                        <TableCell colSpan={12} align="center" style={{ fontStyle: 'italic', color: '#666' }}>
                          No forecast opportunities for next quarter
                        </TableCell>
                      </TableRow>
                    )}
                    {salesForecastNextQuarterData?.forecastOpps?.length > 0 && (
                      <TableRow className={classes.summaryRow}>
                        <TableCell colSpan={3} className={classes.tableCell}>
                          <strong>Total ({salesForecastNextQuarterData.totalCount} opps)</strong>
                        </TableCell>
                        <TableCell className={classes.tableCell} align="right">
                          <strong>{salesForecastNextQuarterData.totalACVInK}</strong>
                        </TableCell>
                        <TableCell colSpan={8} className={classes.tableCell}></TableCell>
                      </TableRow>
                    )}
                    {salesForecastNextQuarterData?.quota > 0 && (
                      <TableRow className={classes.targetRow}>
                        <TableCell colSpan={3} className={classes.tableCell}>
                          <strong>Quota</strong>
                        </TableCell>
                        <TableCell className={classes.tableCell} align="right">
                          <strong>{(salesForecastNextQuarterData.quota / 1000).toFixed(1)}</strong>
                        </TableCell>
                        <TableCell colSpan={8} className={classes.tableCell}></TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Legend for green highlighting */}
              <Box mt={2}>
                <div className={classes.legend}>
                  <div className={classes.legendItem}>
                    <div className={classes.legendBox} style={{ backgroundColor: '#c8e6c9' }} />
                    <Typography variant="body2">New this week</Typography>
                  </div>
                </div>
              </Box>
            </>
          )}
        </Paper>
      </Box>

      {/* Paper 6: Sales Forecast Next Quarter + 1 */}
      <Box mt={4}>
        <Paper className={classes.paper}>
          <Box display="flex" flexDirection="column" alignItems="center" mb={2}>
            <Typography variant="h6" style={{ fontWeight: 600 }}>
              Sales Forecast – Next Quarter +1{salesForecastNextQuarterPlus1Data?.targetQuarter ? ` (${salesForecastNextQuarterPlus1Data.targetQuarter})` : ''} – Opps in Forecast – Most Likely + Commit
            </Typography>
            <span className={classes.updateFrequency}>
              Weekly update - Every Friday PM
            </span>
          </Box>

          {isSalesForecastNextQuarterPlus1Loading ? (
            <Skeleton variant="rect" animation="wave" height={200} width="100%" />
          ) : (
            <>
              {/* Summary */}
              <Box mb={2}>
                <TableContainer>
                  <Table size="small" style={{ maxWidth: 500 }}>
                    <TableHead>
                      <TableRow className={classes.tableHeader}>
                        <TableCell style={{ textAlign: 'center' }}></TableCell>
                        <TableCell style={{ textAlign: 'center' }}># of Opps</TableCell>
                        <TableCell style={{ textAlign: 'center' }}>ACV ($M)</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <TableRow>
                        <TableCell align="center"><strong>Forecast</strong></TableCell>
                        <TableCell align="center">{salesForecastNextQuarterPlus1Data?.totalCount || 0}</TableCell>
                        <TableCell align="center">${salesForecastNextQuarterPlus1Data?.totalACVInMillions || '0.00'}</TableCell>
                      </TableRow>
                      <TableRow style={{ backgroundColor: '#fff9c4' }}>
                        <TableCell align="center"><strong>Quota</strong></TableCell>
                        <TableCell align="center">-</TableCell>
                        <TableCell align="center"><strong>${salesForecastNextQuarterPlus1Data?.quotaInMillions || '0.00'}</strong></TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>

              {/* Forecast Opps Table */}
              <TableContainer className={classes.tableContainer} style={{ maxHeight: '80vh', overflow: 'auto' }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell style={stickyHeaderStyle}>#</TableCell>
                      <TableCell style={stickyHeaderStyle}>Opportunity</TableCell>
                      <TableCell style={stickyHeaderStyle}>Owner</TableCell>
                      <TableCell style={stickyHeaderStyle}>ACV ($K)</TableCell>
                      <TableCell style={stickyHeaderStyle}>Disc. %</TableCell>
                      <TableCell style={stickyHeaderStyle}>Projected<br/>Close Date</TableCell>
                      <TableCell style={stickyHeaderStyle}>Age since<br/>Qual. (Days)</TableCell>
                      <TableCell style={stickyHeaderStyle}>Stage</TableCell>
                      <TableCell style={stickyHeaderStyle}>Days in<br/>Stage</TableCell>
                      <TableCell style={stickyHeaderStyle}>Partner</TableCell>
                      <TableCell style={stickyHeaderStyle}>Forecast<br/>Category</TableCell>
                      <TableCell style={{ ...stickyHeaderStyle, borderRight: 'none' }}>Next Steps</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {salesForecastNextQuarterPlus1Data?.forecastOpps?.slice(0, 15).map((opp, index) => (
                      <TableRow
                        key={opp.oppId || index}
                        className={opp.isNewThisWeek ? classes.newOpportunityRow : classes.tableRow}
                      >
                        <TableCell className={classes.tableCell} align="center">{opp.rowNum}</TableCell>
                        <TableCell className={classes.tableCell} align="left">{opp.opportunity}</TableCell>
                        <TableCell className={classes.tableCell} align="left">{opp.owner}</TableCell>
                        <TableCell className={classes.tableCell} align="right">{opp.acvInK}</TableCell>
                        <TableCell className={classes.tableCell} align="right">{/* Disc. % */}</TableCell>
                        <TableCell className={classes.tableCell} align="right">{opp.projectedCloseDate}</TableCell>
                        <TableCell className={classes.tableCell} align="right">{opp.ageSinceQualDays}</TableCell>
                        <TableCell className={classes.tableCell} align="left">{opp.stageChar}</TableCell>
                        <TableCell className={classes.tableCell} align="right">{/* Days in Stage */}</TableCell>
                        <TableCell className={classes.tableCell} align="left">{opp.partner}</TableCell>
                        <TableCell className={classes.tableCell} align="left">{opp.forecastCategory}</TableCell>
                        <TableCell className={classes.tableCell} align="left" style={{ maxWidth: 200, fontSize: '0.75rem' }}>{/* Next Steps */}</TableCell>
                      </TableRow>
                    ))}
                    {salesForecastNextQuarterPlus1Data?.forecastOpps?.length > 15 && (
                      <TableRow className={classes.tableRow}>
                        <TableCell className={classes.tableCell} align="center"></TableCell>
                        <TableCell className={classes.tableCell} align="left" colSpan={2}>
                          <strong>Other Opps ({salesForecastNextQuarterPlus1Data.forecastOpps.length - 15})</strong>
                        </TableCell>
                        <TableCell className={classes.tableCell} align="right">
                          <strong>
                            {salesForecastNextQuarterPlus1Data.forecastOpps.slice(15).reduce((sum, opp) => sum + parseFloat(opp.acvInK || 0), 0).toFixed(1)}
                          </strong>
                        </TableCell>
                        <TableCell colSpan={8} className={classes.tableCell}></TableCell>
                      </TableRow>
                    )}
                    {(!salesForecastNextQuarterPlus1Data?.forecastOpps || salesForecastNextQuarterPlus1Data.forecastOpps.length === 0) && (
                      <TableRow>
                        <TableCell colSpan={12} align="center" style={{ fontStyle: 'italic', color: '#666' }}>
                          No forecast opportunities for next quarter +1
                        </TableCell>
                      </TableRow>
                    )}
                    {salesForecastNextQuarterPlus1Data?.forecastOpps?.length > 0 && (
                      <TableRow className={classes.summaryRow}>
                        <TableCell colSpan={3} className={classes.tableCell}>
                          <strong>Total ({salesForecastNextQuarterPlus1Data.totalCount} opps)</strong>
                        </TableCell>
                        <TableCell className={classes.tableCell} align="right">
                          <strong>{salesForecastNextQuarterPlus1Data.totalACVInK}</strong>
                        </TableCell>
                        <TableCell colSpan={8} className={classes.tableCell}></TableCell>
                      </TableRow>
                    )}
                    {salesForecastNextQuarterPlus1Data?.quota > 0 && (
                      <TableRow className={classes.targetRow}>
                        <TableCell colSpan={3} className={classes.tableCell}>
                          <strong>Quota</strong>
                        </TableCell>
                        <TableCell className={classes.tableCell} align="right">
                          <strong>{(salesForecastNextQuarterPlus1Data.quota / 1000).toFixed(1)}</strong>
                        </TableCell>
                        <TableCell colSpan={8} className={classes.tableCell}></TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Legend for green highlighting */}
              <Box mt={2}>
                <div className={classes.legend}>
                  <div className={classes.legendItem}>
                    <div className={classes.legendBox} style={{ backgroundColor: '#c8e6c9' }} />
                    <Typography variant="body2">New this week</Typography>
                  </div>
                </div>
              </Box>
            </>
          )}
        </Paper>
      </Box>

      {/* Deal Progression and Closures */}
      <Box mt={4}>
        <Paper className={classes.paper}>
          <Box display="flex" flexDirection="column" alignItems="center" mb={3}>
            <Typography variant="h5" className={classes.title}>
              Deal Progression and Closures
            </Typography>
            <span className={classes.updateFrequency}>
              Monthly and Quarterly update
            </span>
          </Box>

          {isDealProgressionLoading ? (
            <Grid container spacing={3}>
              <Grid item xs={12} md={12}>
                <Skeleton variant="rect" animation="wave" height={300} width="100%" />
              </Grid>
              <Grid item xs={12} md={5}>
                <Skeleton variant="rect" animation="wave" height={280} width="100%" />
              </Grid>
              <Grid item xs={12} md={5}>
                <Skeleton variant="rect" animation="wave" height={280} width="100%" />
              </Grid>
            </Grid>
          ) : (
            <Grid container spacing={3}>
              {/* Chart 1: Cumulative Closed Won by Month */}
              <Grid item xs={12} md={12}>
                <Card className={classes.chartCard}>
                  <CardContent>
                    <div className={classes.chartContainer}>
                      <CumulativeClosedWonChart
                        data={dealProgressionData?.cumulativeClosedWon}
                        fiscalYear={dealProgressionData?.fiscalYear}
                      />
                    </div>
                  </CardContent>
                </Card>
              </Grid>

              {/* Chart 2: Pipeline percent by stage */}
              <Grid item xs={12} md={5}>
                <Card className={classes.chartCard}>
                  <CardContent>
                    <div className={classes.chartContainer}>
                      <PipelineByStageChart
                        data={dealProgressionData?.pipelineByStage}
                        currentFiscalQuarter={dealProgressionData?.currentFiscalQuarter}
                        stageCategories={dealProgressionData?.stageCategories}
                      />
                    </div>
                    {/* Legend for stacked bar - Dynamic */}
                    <Box mt={2}>
                      <div className={classes.legend}>
                        {(() => {
                          const colorScale = d3.scaleOrdinal(updatedScheme10);
                          return dealProgressionData?.stageCategories?.map((category, index) => (
                            <div key={category} className={classes.legendItem}>
                              <div
                                className={classes.legendBox}
                                style={{ backgroundColor: colorScale(category) }}
                              />
                              <Typography variant="body2">{category}</Typography>
                            </div>
                          ));
                        })()}
                      </div>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              {/* Chart 3: Forecast Accuracy */}
              <Grid item xs={12} md={5}>
                <Card className={classes.chartCard}>
                  <CardContent>
                    <div className={classes.chartContainer}>
                      <ForecastAccuracyChart
                        data={dealProgressionData?.forecastAccuracy}
                        target={dealProgressionData?.forecastAccuracyTarget}
                      />
                    </div>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          )}
        </Paper>
      </Box>

      {/* Paper 8: Win-Loss Analysis */}
      <Box mt={4}>
        <Paper className={classes.paper}>
          <Box display="flex" flexDirection="column" alignItems="center" mb={3}>
            <Typography variant="h5" className={classes.title}>
              Win-Loss analysis: Month of XXX
            </Typography>
            <span className={classes.updateFrequency}>
              New page for each month at close
            </span>
          </Box>

          <Grid container spacing={2}>
            {/* Won Opportunities Table */}
            <Grid item xs={12} md={6}>
              <Typography variant="h6" style={{ fontWeight: 600, marginBottom: 8 }}>
                Won Opportunities
              </Typography>
              <TableContainer className={classes.tableContainer}>
                <Table size="small">
                  <TableHead>
                    <TableRow className={classes.tableHeader}>
                      <TableCell>Customer Name</TableCell>
                      <TableCell align="right">ACV ($M)</TableCell>
                      <TableCell>Competition</TableCell>
                      <TableCell>Key Learnings</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow className={classes.tableRow}>
                      <TableCell className={classes.tableCell}>Customer A</TableCell>
                      <TableCell className={classes.tableCell} align="right">1.2</TableCell>
                      <TableCell className={classes.tableCell}>Competitor X</TableCell>
                      <TableCell className={classes.tableCell}>Strong technical fit</TableCell>
                    </TableRow>
                    <TableRow className={classes.tableRow}>
                      <TableCell className={classes.tableCell}>Customer B</TableCell>
                      <TableCell className={classes.tableCell} align="right">0.8</TableCell>
                      <TableCell className={classes.tableCell}>None</TableCell>
                      <TableCell className={classes.tableCell}>Existing relationship</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>

            {/* Lost Opportunities Table */}
            <Grid item xs={12} md={6}>
              <Typography variant="h6" style={{ fontWeight: 600, marginBottom: 8 }}>
                Lost Opportunities
              </Typography>
              <TableContainer className={classes.tableContainer}>
                <Table size="small">
                  <TableHead>
                    <TableRow className={classes.tableHeader}>
                      <TableCell>Customer Name</TableCell>
                      <TableCell align="right">ACV ($M)</TableCell>
                      <TableCell>Competition</TableCell>
                      <TableCell>Key Learnings</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow className={classes.tableRow}>
                      <TableCell className={classes.tableCell}>Customer C</TableCell>
                      <TableCell className={classes.tableCell} align="right">1.5</TableCell>
                      <TableCell className={classes.tableCell}>Competitor Y</TableCell>
                      <TableCell className={classes.tableCell}>Price sensitivity</TableCell>
                    </TableRow>
                    <TableRow className={classes.tableRow}>
                      <TableCell className={classes.tableCell}>Customer D</TableCell>
                      <TableCell className={classes.tableCell} align="right">0.9</TableCell>
                      <TableCell className={classes.tableCell}>Competitor Z</TableCell>
                      <TableCell className={classes.tableCell}>Feature gaps</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>
          </Grid>
        </Paper>
      </Box>

      {/* Paper 9: MSSP Fulfillment */}
      <Box mt={4}>
        <Paper className={classes.paper}>
          <Box display="flex" flexDirection="column" alignItems="center" mb={3}>
            <Typography variant="h5" className={classes.title}>
              MSSP Fulfillment – Cumulative ACV ($M)
            </Typography>
            <span className={classes.updateFrequency}>
              Monthly update
            </span>
          </Box>

          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Card className={classes.chartCard}>
                <CardContent>
                  <Box className={classes.chartContainer} style={{ minHeight: 350 }}>
                    <Skeleton variant="rect" animation="wave" height={300} width="100%" />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Paper>
      </Box>

      {/* Paper 10: Renewals Tracker Summary */}
      <Box mt={4}>
        <Paper className={classes.paper}>
          <Box display="flex" flexDirection="column" alignItems="center" mb={3}>
            <Typography variant="h5" className={classes.title}>
              Renewals tracker summary
            </Typography>
            <span className={classes.updateFrequency}>
              Monthly update
            </span>
          </Box>

          <Grid container spacing={3}>
            {/* Monthly Renewals Chart */}
            <Grid item xs={12} md={6}>
              <Card className={classes.chartCard}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Monthly Renewals ($M)
                  </Typography>
                  <Box className={classes.chartContainer}>
                    <Skeleton variant="rect" animation="wave" height={250} width="100%" />
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Cumulative Renewals Chart */}
            <Grid item xs={12} md={6}>
              <Card className={classes.chartCard}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Cumulative Renewals ($M)
                  </Typography>
                  <Box className={classes.chartContainer}>
                    <Skeleton variant="rect" animation="wave" height={250} width="100%" />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Paper>
      </Box>

      {/* Paper 11: Renewals Tracker Detail */}
      <Box mt={4}>
        <Paper className={classes.paper}>
          <Box display="flex" flexDirection="column" alignItems="center" mb={3}>
            <Typography variant="h5" className={classes.title}>
              Renewals tracker detail – Month of XXX
            </Typography>
            <span className={classes.updateFrequency}>
              Monthly update
            </span>
          </Box>

          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TableContainer className={classes.tableContainer}>
                <Table size="small">
                  <TableHead>
                    <TableRow className={classes.tableHeader}>
                      <TableCell>#</TableCell>
                      <TableCell>Customer Name</TableCell>
                      <TableCell align="center">Renewal Date</TableCell>
                      <TableCell align="right">Contract Value ($M)</TableCell>
                      <TableCell align="center">Probability (%)</TableCell>
                      <TableCell align="center">Status</TableCell>
                      <TableCell>Risk Factors</TableCell>
                      <TableCell>Action Items</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow className={classes.tableRow}>
                      <TableCell className={classes.tableCell}>1</TableCell>
                      <TableCell className={classes.tableCell}>Customer Alpha</TableCell>
                      <TableCell className={classes.tableCell} align="center">2026-03-15</TableCell>
                      <TableCell className={classes.tableCell} align="right">2.5</TableCell>
                      <TableCell className={classes.tableCell} align="center">90</TableCell>
                      <TableCell className={classes.tableCell} align="center">On Track</TableCell>
                      <TableCell className={classes.tableCell}>None</TableCell>
                      <TableCell className={classes.tableCell}>Schedule QBR</TableCell>
                    </TableRow>
                    <TableRow className={classes.tableRow}>
                      <TableCell className={classes.tableCell}>2</TableCell>
                      <TableCell className={classes.tableCell}>Customer Beta</TableCell>
                      <TableCell className={classes.tableCell} align="center">2026-03-20</TableCell>
                      <TableCell className={classes.tableCell} align="right">1.8</TableCell>
                      <TableCell className={classes.tableCell} align="center">75</TableCell>
                      <TableCell className={classes.tableCell} align="center">At Risk</TableCell>
                      <TableCell className={classes.tableCell}>Budget concerns</TableCell>
                      <TableCell className={classes.tableCell}>Executive engagement needed</TableCell>
                    </TableRow>
                    <TableRow className={classes.tableRow}>
                      <TableCell className={classes.tableCell}>3</TableCell>
                      <TableCell className={classes.tableCell}>Customer Gamma</TableCell>
                      <TableCell className={classes.tableCell} align="center">2026-03-25</TableCell>
                      <TableCell className={classes.tableCell} align="right">3.2</TableCell>
                      <TableCell className={classes.tableCell} align="center">95</TableCell>
                      <TableCell className={classes.tableCell} align="center">Committed</TableCell>
                      <TableCell className={classes.tableCell}>None</TableCell>
                      <TableCell className={classes.tableCell}>Send contract</TableCell>
                    </TableRow>
                    <TableRow className={classes.summaryRow}>
                      <TableCell colSpan={3} className={classes.tableCell}>
                        <strong>TOTAL</strong>
                      </TableCell>
                      <TableCell className={classes.tableCell} align="right">
                        <strong>7.5</strong>
                      </TableCell>
                      <TableCell colSpan={4} className={classes.tableCell}></TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>
          </Grid>
        </Paper>
      </Box>

      {/* Paper 12: Upsell-Cross Sell Tracker */}
      <Box mt={4}>
        <Paper className={classes.paper}>
          <Box display="flex" flexDirection="column" alignItems="center" mb={3}>
            <Typography variant="h5" className={classes.title}>
              Upsell-Cross Sell Tracker
            </Typography>
            <span className={classes.updateFrequency}>
              Monthly update
            </span>
          </Box>

          {isUpsellCrossSellLoading ? (
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Skeleton variant="rect" animation="wave" height={300} width="100%" />
              </Grid>
            </Grid>
          ) : (
            <Grid container spacing={3}>
              {(() => {
                // When "All" is selected, show Company data; otherwise show selected team's data
                const teamKey = appliedTeam && appliedTeam !== 'All' ? appliedTeam : 'Company';
                const teamData = upsellCrossSellData?.teams?.[teamKey];
                const displayName = teamKey === 'Company' ? 'ColorTokens' : teamKey;

                if (!teamData) return null;

                return (
                  <Grid item xs={12} >
                    <Card className={classes.chartCard}>
                      <CardContent>
                        <div className={classes.chartContainer} id="printChart">
                          <UpsellCrossSellChart
                            data={teamData}
                            teamName={displayName}
                          />
                        </div>
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })()}
            </Grid>
          )}

          {/* Legend for cumulative target vs actual */}
          <Box mt={2}>
            <div className={classes.legend}>
              <div className={classes.legendItem}>
                <div
                  className={classes.legendBox}
                  style={{ backgroundColor: '#bf9000' }}
                />
                <Typography variant="body2">Cumulative (target)</Typography>
              </div>
              <div className={classes.legendItem}>
                <div
                  className={classes.legendBox}
                  style={{ backgroundColor: '#70ad47' }}
                />
                <Typography variant="body2">Cumulative (actual)</Typography>
              </div>
            </div>
          </Box>
        </Paper>
      </Box>

      {/* Paper 13: Upsell-Cross Sell Tracker Detail */}
      <Box mt={4}>
        <Paper className={classes.paper}>
          <Box display="flex" flexDirection="column" alignItems="center" mb={3}>
            <Typography variant="h5" className={classes.title}>
              Upsell-Cross Sell tracker detail
            </Typography>
            <span className={classes.updateFrequency}>
              Monthly update
            </span>
          </Box>

          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TableContainer className={classes.tableContainer} style={{ overflowX: 'auto' }}>
                <Table size="small" style={{ minWidth: '2000px' }}>
                  <TableHead>
                    <TableRow className={classes.tableHeader}>
                      <TableCell align="center" style={{ position: 'sticky', left: 0, backgroundColor: '#f5f5f5', zIndex: 2 }}></TableCell>
                      <TableCell align="center" colSpan={3} style={{ borderRight: '2px solid #ddd' }}>February</TableCell>
                      <TableCell align="center" colSpan={3} style={{ borderRight: '2px solid #ddd' }}>March</TableCell>
                      <TableCell align="center" colSpan={3} style={{ borderRight: '2px solid #ddd' }}>April</TableCell>
                      <TableCell align="center" colSpan={3} style={{ borderRight: '2px solid #ddd' }}>May</TableCell>
                      <TableCell align="center" colSpan={3} style={{ borderRight: '2px solid #ddd' }}>June</TableCell>
                      <TableCell align="center" colSpan={3} style={{ borderRight: '2px solid #ddd' }}>July</TableCell>
                    </TableRow>
                    <TableRow className={classes.tableHeader}>
                      <TableCell align="center" style={{ position: 'sticky', left: 0, backgroundColor: '#f5f5f5', zIndex: 2 }}></TableCell>
                      <TableCell align="center">Customer</TableCell>
                      <TableCell align="center">Target</TableCell>
                      <TableCell align="center" style={{ borderRight: '2px solid #ddd' }}>Actual</TableCell>
                      <TableCell align="center">Customer</TableCell>
                      <TableCell align="center">Target</TableCell>
                      <TableCell align="center" style={{ borderRight: '2px solid #ddd' }}>Actual</TableCell>
                      <TableCell align="center">Customer</TableCell>
                      <TableCell align="center">Target</TableCell>
                      <TableCell align="center" style={{ borderRight: '2px solid #ddd' }}>Actual</TableCell>
                      <TableCell align="center">Customer</TableCell>
                      <TableCell align="center">Target</TableCell>
                      <TableCell align="center" style={{ borderRight: '2px solid #ddd' }}>Actual</TableCell>
                      <TableCell align="center">Customer</TableCell>
                      <TableCell align="center">Target</TableCell>
                      <TableCell align="center" style={{ borderRight: '2px solid #ddd' }}>Actual</TableCell>
                      <TableCell align="center">Customer</TableCell>
                      <TableCell align="center">Target</TableCell>
                      <TableCell align="center" style={{ borderRight: '2px solid #ddd' }}>Actual</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {[1, 2, 3, 4, 5, 6].map((rowNum) => (
                      <TableRow key={rowNum} className={classes.tableRow}>
                        <TableCell className={classes.tableCell} style={{ position: 'sticky', left: 0, backgroundColor: '#fff', zIndex: 1 }}></TableCell>
                        {[...Array(6)].map((_, monthIdx) => (
                          <React.Fragment key={monthIdx}>
                            <TableCell className={classes.tableCell}></TableCell>
                            <TableCell className={classes.tableCell}></TableCell>
                            <TableCell className={classes.tableCell} style={{ borderRight: monthIdx < 5 ? '2px solid #ddd' : 'none' }}></TableCell>
                          </React.Fragment>
                        ))}
                      </TableRow>
                    ))}
                    <TableRow className={classes.summaryRow}>
                      <TableCell className={classes.tableCell} style={{ position: 'sticky', left: 0, backgroundColor: '#f5f5f5', zIndex: 1 }}>
                        <strong>TOTAL =</strong>
                      </TableCell>
                      {[...Array(6)].map((_, monthIdx) => (
                        <React.Fragment key={monthIdx}>
                          <TableCell className={classes.tableCell}></TableCell>
                          <TableCell className={classes.tableCell}></TableCell>
                          <TableCell className={classes.tableCell} style={{ borderRight: monthIdx < 5 ? '2px solid #ddd' : 'none' }}></TableCell>
                        </React.Fragment>
                      ))}
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>
          </Grid>

          <Box mt={4}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TableContainer className={classes.tableContainer} style={{ overflowX: 'auto' }}>
                  <Table size="small" style={{ minWidth: '2000px' }}>
                    <TableHead>
                      <TableRow className={classes.tableHeader}>
                        <TableCell align="center" style={{ position: 'sticky', left: 0, backgroundColor: '#f5f5f5', zIndex: 2 }}></TableCell>
                        <TableCell align="center" colSpan={3} style={{ borderRight: '2px solid #ddd' }}>August</TableCell>
                        <TableCell align="center" colSpan={3} style={{ borderRight: '2px solid #ddd' }}>September</TableCell>
                        <TableCell align="center" colSpan={3} style={{ borderRight: '2px solid #ddd' }}>October</TableCell>
                        <TableCell align="center" colSpan={3} style={{ borderRight: '2px solid #ddd' }}>November</TableCell>
                        <TableCell align="center" colSpan={3} style={{ borderRight: '2px solid #ddd' }}>December</TableCell>
                        <TableCell align="center" colSpan={3} style={{ borderRight: '2px solid #ddd' }}>January</TableCell>
                      </TableRow>
                      <TableRow className={classes.tableHeader}>
                        <TableCell align="center" style={{ position: 'sticky', left: 0, backgroundColor: '#f5f5f5', zIndex: 2 }}></TableCell>
                        <TableCell align="center">Customer</TableCell>
                        <TableCell align="center">Target</TableCell>
                        <TableCell align="center" style={{ borderRight: '2px solid #ddd' }}>Actual</TableCell>
                        <TableCell align="center">Customer</TableCell>
                        <TableCell align="center">Target</TableCell>
                        <TableCell align="center" style={{ borderRight: '2px solid #ddd' }}>Actual</TableCell>
                        <TableCell align="center">Customer</TableCell>
                        <TableCell align="center">Target</TableCell>
                        <TableCell align="center" style={{ borderRight: '2px solid #ddd' }}>Actual</TableCell>
                        <TableCell align="center">Customer</TableCell>
                        <TableCell align="center">Target</TableCell>
                        <TableCell align="center" style={{ borderRight: '2px solid #ddd' }}>Actual</TableCell>
                        <TableCell align="center">Customer</TableCell>
                        <TableCell align="center">Target</TableCell>
                        <TableCell align="center" style={{ borderRight: '2px solid #ddd' }}>Actual</TableCell>
                        <TableCell align="center">Customer</TableCell>
                        <TableCell align="center">Target</TableCell>
                        <TableCell align="center" style={{ borderRight: '2px solid #ddd' }}>Actual</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {[1, 2, 3, 4, 5, 6].map((rowNum) => (
                        <TableRow key={rowNum} className={classes.tableRow}>
                          <TableCell className={classes.tableCell} style={{ position: 'sticky', left: 0, backgroundColor: '#fff', zIndex: 1 }}></TableCell>
                          {[...Array(6)].map((_, monthIdx) => (
                            <React.Fragment key={monthIdx}>
                              <TableCell className={classes.tableCell}></TableCell>
                              <TableCell className={classes.tableCell}></TableCell>
                              <TableCell className={classes.tableCell} style={{ borderRight: monthIdx < 5 ? '2px solid #ddd' : 'none' }}></TableCell>
                            </React.Fragment>
                          ))}
                        </TableRow>
                      ))}
                      <TableRow className={classes.summaryRow}>
                        <TableCell className={classes.tableCell} style={{ position: 'sticky', left: 0, backgroundColor: '#f5f5f5', zIndex: 1 }}>
                          <strong>TOTAL =</strong>
                        </TableCell>
                        {[...Array(6)].map((_, monthIdx) => (
                          <React.Fragment key={monthIdx}>
                            <TableCell className={classes.tableCell}></TableCell>
                            <TableCell className={classes.tableCell}></TableCell>
                            <TableCell className={classes.tableCell} style={{ borderRight: monthIdx < 5 ? '2px solid #ddd' : 'none' }}></TableCell>
                          </React.Fragment>
                        ))}
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </Grid>
            </Grid>
          </Box>
        </Paper>
      </Box>
    </div>
  );
};

const mapStateToProps = (state) => {
  const { user, forecastTrendData, salesForecastCurrentQuarterData, forecastMovedOutData, salesForecastNextQuarterData, salesForecastNextQuarterPlus1Data, dealProgressionData, dealConversionData, upsellCrossSellData, rsrFirstLevelTeams, rsrSelectedTeam } = state.app;

  return {
    company: user.company,
    Value_Label: user.Value_Label,
    forecastTrendData,
    salesForecastCurrentQuarterData,
    forecastMovedOutData,
    salesForecastNextQuarterData,
    salesForecastNextQuarterPlus1Data,
    dealProgressionData,
    dealConversionData,
    upsellCrossSellData,
    rsrFirstLevelTeams,
    rsrSelectedTeam,
  };
};

const mapDispatchToProps = (dispatch) => ({
  getFirstLevelTeams: () => {
    dispatch({ type: "get_rsr_first_level_teams_request" });
    appService.getRegionalSalesReviewFirstLevelTeams().then(
      (json) => {
        dispatch({ type: "get_rsr_first_level_teams_success", json });
      },
      (error) => {
        if (typeof error === "object")
          dispatch({
            type: "get_rsr_first_level_teams_failure",
            error: "Something went wrong",
          });
        else dispatch({ type: "get_rsr_first_level_teams_failure", error });
      }
    );
  },
  getForecastTrendData: (filters = []) => {
    dispatch({ type: "get_forecast_trend_data_request" });
    appService.getRegionalSalesReviewForecastTrend(filters).then(
      (json) => {
        dispatch({ type: "get_forecast_trend_data_success", json });
      },
      (error) => {
        if (typeof error === "object")
          dispatch({
            type: "get_forecast_trend_data_failure",
            error: "Something went wrong",
          });
        else dispatch({ type: "get_forecast_trend_data_failure", error });
      }
    );
  },
  getSalesForecastCurrentQuarterData: (filters = []) => {
    dispatch({ type: "get_sales_forecast_current_quarter_data_request" });
    appService.getRegionalSalesReviewSalesForecastCurrentQuarter(filters).then(
      (json) => {
        dispatch({ type: "get_sales_forecast_current_quarter_data_success", json });
      },
      (error) => {
        if (typeof error === "object")
          dispatch({
            type: "get_sales_forecast_current_quarter_data_failure",
            error: "Something went wrong",
          });
        else dispatch({ type: "get_sales_forecast_current_quarter_data_failure", error });
      }
    );
  },
  getDealProgressionData: (filters = []) => {
    dispatch({ type: "get_deal_progression_data_request" });
    appService.getRegionalSalesReviewDealProgression(filters).then(
      (json) => {
        dispatch({ type: "get_deal_progression_data_success", json });
      },
      (error) => {
        if (typeof error === "object")
          dispatch({
            type: "get_deal_progression_data_failure",
            error: "Something went wrong",
          });
        else dispatch({ type: "get_deal_progression_data_failure", error });
      }
    );
  },
  getDealConversionData: (filters = []) => {
    dispatch({ type: "get_deal_conversion_data_request" });
    appService.getRegionalSalesReviewDealConversion(filters).then(
      (json) => {
        dispatch({ type: "get_deal_conversion_data_success", json });
      },
      (error) => {
        if (typeof error === "object")
          dispatch({
            type: "get_deal_conversion_data_failure",
            error: "Something went wrong",
          });
        else dispatch({ type: "get_deal_conversion_data_failure", error });
      }
    );
  },
  getUpsellCrossSellData: (filters = []) => {
    dispatch({ type: "get_upsell_cross_sell_data_request" });
    appService.getRegionalSalesReviewUpsellCrossSell(filters).then(
      (json) => {
        dispatch({ type: "get_upsell_cross_sell_data_success", json });
      },
      (error) => {
        if (typeof error === "object")
          dispatch({
            type: "get_upsell_cross_sell_data_failure",
            error: "Something went wrong",
          });
        else dispatch({ type: "get_upsell_cross_sell_data_failure", error });
      }
    );
  },
  getForecastMovedOutData: (filters = []) => {
    dispatch({ type: "get_forecast_moved_out_data_request" });
    appService.getRegionalSalesReviewForecastMovedOut(filters).then(
      (json) => {
        dispatch({ type: "get_forecast_moved_out_data_success", json });
      },
      (error) => {
        if (typeof error === "object")
          dispatch({
            type: "get_forecast_moved_out_data_failure",
            error: "Something went wrong",
          });
        else dispatch({ type: "get_forecast_moved_out_data_failure", error });
      }
    );
  },
  getSalesForecastNextQuarterData: (filters = []) => {
    dispatch({ type: "get_sales_forecast_next_quarter_data_request" });
    appService.getRegionalSalesReviewSalesForecastNextQuarter(filters).then(
      (json) => {
        dispatch({ type: "get_sales_forecast_next_quarter_data_success", json });
      },
      (error) => {
        if (typeof error === "object")
          dispatch({
            type: "get_sales_forecast_next_quarter_data_failure",
            error: "Something went wrong",
          });
        else dispatch({ type: "get_sales_forecast_next_quarter_data_failure", error });
      }
    );
  },
  getSalesForecastNextQuarterPlus1Data: (filters = []) => {
    dispatch({ type: "get_sales_forecast_next_quarter_plus1_data_request" });
    appService.getRegionalSalesReviewSalesForecastNextQuarterPlus1(filters).then(
      (json) => {
        dispatch({ type: "get_sales_forecast_next_quarter_plus1_data_success", json });
      },
      (error) => {
        if (typeof error === "object")
          dispatch({
            type: "get_sales_forecast_next_quarter_plus1_data_failure",
            error: "Something went wrong",
          });
        else dispatch({ type: "get_sales_forecast_next_quarter_plus1_data_failure", error });
      }
    );
  },
});

const connectedRegionalSalesReviewPackage = connect(
  mapStateToProps,
  mapDispatchToProps
)(withStyles(styles)(RegionalSalesReviewPackage));

export default connectedRegionalSalesReviewPackage;